import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { services, calendlyLink } from '../mock';
import { ArrowLeft, CheckCircle } from 'lucide-react';

const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="section-padding">
        <div className="container">
          <p>Service not found</p>
          <Link to="/services">Back to Services</Link>
        </div>
      </div>
    );
  }

  // Hero Section Component with Full Image and title at middle-left (vertically centered)
  const HeroSection = ({ title, subtitle, image }) => (
    <>
      <section style={{ padding: '20px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <Link to="/services" style={{
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ArrowLeft size={14} /> Back to Services
          </Link>
        </div>
      </section>

      {/* Full Image Section with title at middle-left (vertically centered) */}
      <section style={{ position: 'relative', minHeight: '500px' }}>
        <img 
          src={image} 
          alt={title}
          style={{
            width: '100%',
            height: 'auto',
            minHeight: '500px',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        {/* Title overlay - vertically centered, left-aligned */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '0',
          transform: 'translateY(-50%)',
          width: '100%',
          padding: '0 5%'
        }}>
          <div style={{
            maxWidth: '550px'
          }}>
            <h1 className="hero-large" style={{ 
              marginBottom: '16px',
              color: '#FFFEF2',
              textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3)'
            }}>
              {title}
            </h1>
            <p className="body-large" style={{ 
              color: '#FFFEF2',
              textShadow: '0 1px 10px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.3)',
              fontWeight: '500'
            }}>
              {subtitle}
            </p>
          </div>
        </div>
      </section>
    </>
  );

  // CTA Section Component
  const CTASection = ({ title = "Ready to Begin?", buttonText = "Book Your Discovery Session" }) => (
    <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
      <div className="container" style={{ textAlign: 'center' }}>
        <h2 className="heading-1" style={{ marginBottom: '24px' }}>{title}</h2>
        <a href={calendlyLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{
          background: 'transparent',
          color: 'var(--interactive-base)',
          border: '1px solid var(--interactive-base)',
          borderRadius: '0px',
          padding: '19px 23px',
          minWidth: '210px',
          height: '60px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {buttonText}
        </a>
      </div>
      <style>{`
        .btn-primary:hover {
          background: var(--interactive-base) !important;
          color: var(--bg-primary) !important;
        }
        @media (max-width: 1024px) {
          .responsive-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </section>
  );

  // Render Private Hypnotherapy
  if (serviceId === 'private-hypnotherapy') {
    return (
      <div>
        <HeroSection 
          title={service.title}
          subtitle={service.shortDescription}
          image={service.image}
        />

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              If you have been trying to think your way out of something — and it is not shifting — the pattern is likely deeper.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              Private hypnotherapy at Get Lucid is structured, individualized, and clinically informed. This is not motivational coaching. It is targeted subconscious work designed to change the conditioned responses driving stress, fear, habits, and performance blocks.
            </p>
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500, marginBottom: '48px' }}>
              We work below the surface — where your automatic responses live.
            </p>

            <h2 className="heading-1" style={{ marginBottom: '32px' }}>
              The Integrative Approach
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', marginBottom: '24px' }}>
              Each session draws from a range of modalities, tailored to your needs:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '48px' }}>
              {service.content.features.map((feature, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
          <div className="container">
            <h2 className="heading-1" style={{ marginBottom: '48px' }}>
              Session Options
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }} className="responsive-grid">
              <div style={{ padding: '40px', background: 'var(--bg-primary)' }}>
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>{service.content.options.single.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-meta)', marginBottom: '24px', fontWeight: 500 }}>
                  {service.content.options.single.subtitle}
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  Best for:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>One specific trigger or event</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Confidence resets</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Pre-performance support</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Clarity on a repeating pattern</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Targeted breakthroughs</span>
                  </li>
                </ul>
              </div>
              <div style={{ padding: '40px', background: 'var(--bg-primary)' }}>
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>{service.content.options.structured.title}</h3>
                <p className="body-regular" style={{ color: 'var(--text-meta)', marginBottom: '24px', fontWeight: 500 }}>
                  {service.content.options.structured.subtitle}
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '16px' }}>
                  Best for:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Long-standing anxiety cycles</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Trauma-informed work</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Repeated emotional triggers</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Habit change (smoking, eating, self-sabotage)</span>
                  </li>
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Sleep regulation</span>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: 'var(--text-primary)' }}>•</span>
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>Performance conditioning</span>
                  </li>
                </ul>
                <p className="body-small" style={{ color: 'var(--text-meta)', marginTop: '16px', fontStyle: 'italic' }}>
                  Typically includes 3–8 sessions, depending on complexity.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '24px' }}>
              {service.content.successRate}
            </p>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              {service.content.note}
            </p>
          </div>
        </section>

        <CTASection title="Ready to Begin?" buttonText="Book Your Free Discovery Session" />
      </div>
    );
  }

  // Render Corporate Programs
  if (serviceId === 'corporate-programs') {
    return (
      <div>
        <HeroSection 
          title={service.title}
          subtitle={service.shortDescription}
          image={service.image}
        />

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500, marginBottom: '24px' }}>
              Performance begins in the mind. Sustainable excellence begins in the nervous system.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              Elite Subconscious Performance Architecture is a strategic recalibration framework designed for organizations operating at high velocity — where clarity, resilience, and emotional regulation are not optional.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '48px' }}>
              Whether you lead a growing startup, a legacy enterprise, or a high-performance team under sustained pressure — your people's internal state directly determines your external results.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              {service.content.crisis.title}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, marginBottom: '48px' }}>
              {service.content.crisis.stats.map((stat, index) => (
                <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{stat}</span>
                </li>
              ))}
            </ul>
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', fontWeight: 500 }}>
              Productivity training alone doesn't solve a nervous system problem.
            </p>
          </div>
        </section>

        <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
          <div className="container">
            <h2 className="heading-1" style={{ marginBottom: '48px' }}>
              Corporate Programs
            </h2>
            {service.content.programs.map((program, index) => (
              <div key={index} style={{
                padding: '40px',
                background: 'var(--bg-primary)',
                marginBottom: '24px'
              }}>
                <h3 className="heading-2" style={{ marginBottom: '8px' }}>{program.name}</h3>
                <p className="body-regular" style={{ color: 'var(--text-meta)', marginBottom: '16px', fontWeight: 500 }}>
                  {program.duration}
                </p>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  <strong>Ideal for:</strong> {program.ideal}
                </p>
                <div>
                  <strong className="body-regular">Outcomes:</strong>
                  <ul style={{ listStyle: 'none', padding: 0, marginTop: '8px' }}>
                    {program.outcomes.map((outcome, i) => (
                      <li key={i} style={{ marginBottom: '4px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <span style={{ color: 'var(--text-primary)' }}>•</span>
                        <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section-padding">
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              Interested in Corporate Programming?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
              Contact us to discuss custom solutions for your organization.
            </p>
            <a href={calendlyLink} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{
              background: 'transparent',
              color: 'var(--interactive-base)',
              border: '1px solid var(--interactive-base)',
              borderRadius: '0px',
              padding: '19px 23px',
              minWidth: '210px',
              height: '60px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              Book A Discovery Call
            </a>
          </div>
        </section>
      </div>
    );
  }

  // Render Transformational Healing
  if (serviceId === 'transformational-healing') {
    return (
      <div>
        <HeroSection 
          title={service.title}
          subtitle={service.shortDescription}
          image={service.image}
        />

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              Transformational Healing is designed for individuals who feel called to deeper inner work — beyond symptom relief, beyond habit change.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              This is for those navigating awakening, identity shifts, spiritual curiosity, emotional release, or a desire to understand themselves at a more profound level.
            </p>
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500, marginBottom: '48px' }}>
              This is not therapy. It is not coaching. It is structured inner work with spiritual depth.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              The Approach
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', marginBottom: '24px' }}>
              Sessions may include:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '48px' }}>
              {service.content.approach.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
          <div className="container">
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Who This Is For
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              This work is designed for individuals who:
            </p>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {service.content.whoItsFor.map((item, index) => (
                <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <CTASection />
      </div>
    );
  }

  // Render Mindset Coaching
  if (serviceId === 'mindset-coaching') {
    return (
      <div>
        <HeroSection 
          title={service.title}
          subtitle={service.shortDescription}
          image={service.image}
        />

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              Mindset Coaching at Get Lucid is a monthly subscription designed for people who want consistent clarity, direction, and accountability.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              This is not therapy. It is not vague motivation. It is structured, high-level thinking support for individuals who want to perform better, make stronger decisions, and build momentum.
            </p>
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500, marginBottom: '48px' }}>
              Think of it as strategic counsel — for the internal game.
            </p>

            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              What This Is
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '48px' }}>
              {service.content.whatThisIs.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{item}</span>
                </div>
              ))}
            </div>

            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Session Focus Areas
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {service.content.sessions.map((item, index) => (
                <div key={index} style={{ padding: '16px', background: 'var(--bg-secondary)' }}>
                  <p className="body-regular">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
          <div className="container">
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Who This Is For
            </h2>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {service.content.whoItsFor.map((item, index) => (
                <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                  <span className="body-regular">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <CTASection />
      </div>
    );
  }

  // Render Feng Shui
  if (serviceId === 'feng-shui-guidance') {
    return (
      <div>
        <HeroSection 
          title={service.title}
          subtitle={service.shortDescription}
          image={service.image}
        />

        <section className="section-padding">
          <div className="container">
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
              Feng Shui is the art and science of spatial alignment. Originating over 3,000 years ago in ancient China, it examines how layout, orientation, light, materials, and spatial flow influence human behavior, emotion, and clarity.
            </p>
            <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500, marginBottom: '48px' }}>
              {service.content.philosophy}
            </p>
          </div>
        </section>

        <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }} className="responsive-grid">
              <div>
                <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                  For Your Home
                </h2>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {service.content.forHome.map((item, index) => (
                    <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                  For Your Office
                </h2>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {service.content.forOffice.map((item, index) => (
                    <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckCircle size={20} style={{ color: 'var(--text-primary)', flexShrink: 0, marginTop: '2px' }} />
                      <span className="body-regular">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <CTASection title="Ready to Transform Your Space?" buttonText="Book A Consultation" />
      </div>
    );
  }

  // Fallback
  return <div className="container section-padding">Service content coming soon</div>;
};

export default ServiceDetailPage;
